import { Inject, Injectable, Optional } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map } from 'rxjs';
import { User, SimpleUser } from 'src/app/models';
import { userToSimplifiedUser } from '../helpers/user.helper';
import { IUserApi } from '../api/interfaces/user-abastract.api';
import { NEVER_TOKEN } from '../constants/injection-tokens';
import { AbstractFacade } from './abstract.facade';
import { FollowApi } from '../api/follow.api';

export interface UserState {
  user: User;
  loading: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UserFacade extends AbstractFacade {
  private state: UserState = {
    user: null,
    loading: false,
  };
  public readonly store = new BehaviorSubject<UserState>(this.state);
  public readonly state$ = this.store.asObservable();
  public readonly user$ = this.state$.pipe(
    map((state) => state.user),
    distinctUntilChanged()
  );
  public readonly loading$ = this.state$.pipe(
    map((state) => state.loading),
    distinctUntilChanged()
  );

  constructor(
    private readonly userApi: IUserApi,
    @Inject(NEVER_TOKEN) @Optional() public userId: number
  ) {
    super();
  }

  setState(newState: Partial<UserState>) {
    this.state = {
      ...this.state,
      ...newState,
    };
    this.store.next(this.state);
  }

  getState(): UserState {
    return this.state;
  }

  getUser(force: boolean = false) {
    if (this.getSimplifiedUser()) {
    }
  }

  loadUserInformation() {
    this.setState({
      loading: true,
    });
    this.userApi.getUser(this.userId).subscribe((user) => {
      this.setState({
        user,
        loading: false,
      });
    });
  }

  isLoaded() {
    return this.state.user !== null;
  }

  isLoading() {
    return this.state.loading;
  }

  getSimplifiedUser(): SimpleUser {
    if (!this.isLoaded()) {
      return null;
    }
    return userToSimplifiedUser(this.state.user);
  }

  updateUser(user: Partial<User>) {
    this.setState({
      user: {
        ...this.state.user,
        ...user,
      },
    });
  }

  followed() {
    const userId = this.state.user?.id;
    if (!userId) {
      return;
    }
    this.setState({
      user: {
        ...this.state.user,
        is_followed: true,
        followers_count: this.state.user.followers_count + 1,
      },
    });
  }

  unfollowed() {
    const userId = this.state.user?.id;
    if (!userId) {
      return;
    }
    this.setState({
      user: {
        ...this.state.user,
        is_followed: false,
        followers_count: this.state.user.followers_count - 1,
      },
    });
  }

  externalUserFollowed() {
    this.setState({
      user: {
        ...this.state.user,
        followings_count: this.state.user.followings_count + 1,
      },
    });
  }

  externalUserUnfollowed() {
    this.setState({
      user: {
        ...this.state.user,
        followings_count: this.state.user.followings_count - 1,
      },
    });
  }

  reset() {
    this.setState({
      user: null,
      loading: false,
    });
  }
}
